import { Share2, Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { useOrderSourcesTable } from '@/hooks/features/order-source/useOrderSourcesTable'
import { useDataTable } from '@/hooks/ui'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { ActionDropdown, ConfirmDialog, PageHeader, StatsCards, StatusBadge } from '@/components/atoms'
import { OrderSourceModel } from '@/types'
import { OrderSourceFilters } from './OrderSourceFilters'
import { OrderSourceDialog } from './OrderSourceDialog'

type TableColumns = 'name' | 'status' | 'orders_count' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
    'name',
    'status',
    'orders_count',
    'actions',
]

export const OrderSourcesTable = () => {
    const {
        loading,
        data,
        pagination,
        formMethods,
        stats,
        mode,
        orderSourceDialogOpen,
        editOrderSource,
        deleteDialogOpen,
        orderSourceToDelete,
        createLoading,
        updateLoading,
        deleteLoading,
        setPage,
        setPageSize,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleDeleteClick,
        handleDeleteConfirm,
        setDeleteDialogOpen,
        handleCreateOrderSource,
        handleUpdateOrderSource,
        toggleStatus,
    } = useOrderSourcesTable()

    const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
        OrderSourceModel.OrderSource,
        TableColumns
    >({
        columns: [
            {
                accessorKey: 'name',
                columnLabel: () => 'Source',
                cell: (data) => <span className="font-medium text-ink">{data?.name}</span>,
                hasSorting: true,
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (data) => (
                    <div className="flex items-center gap-2">
                        <StatusBadge status={data?.status === 'active' ? 'active' : 'inactive'} />
                        <Switch
                            checked={data?.status === 'active'}
                            onCheckedChange={() => toggleStatus(data)}
                        />
                    </div>
                ),
            },
            {
                accessorKey: 'orders_count',
                columnLabel: () => 'Orders',
                cell: (data) => <span className="tabular-nums text-ink">{data?.orders_count || 0}</span>,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (data) => (
                    <div className="flex justify-end">
                        <ActionDropdown
                            items={[
                                {
                                    label: 'Edit',
                                    permission: 'order_sources:update',
                                    icon: Pencil,
                                    onClick: () => openEditDialog(data),
                                },
                                {
                                    label: 'Delete',
                                    permission: 'order_sources:delete',
                                    icon: Trash2,
                                    variant: 'destructive',
                                    onClick: () => handleDeleteClick(data),
                                },
                            ]}
                        />
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns: DEFAULT_VISIBLE_COLUMNS,
        disabledColumns: ['actions'],
    })

    return (
        <div className="space-y-4">
            <PageHeader
                title="Order sources"
                description="Where your orders come from, such as a website, Facebook or a physical store."
                onAddClick={openCreateDialog}
                addPermission="order_sources:create"
                addButtonText="Add source"
            />

            <StatsCards
                stats={[
                    {
                        title: 'Total sources',
                        value: stats.totalOrderSources,
                        icon: Share2,
                    },
                    {
                        title: 'Active sources',
                        value: stats.activeOrderSources,
                    },
                    {
                        title: 'Inactive sources',
                        value: stats.inactiveOrderSources,
                    },
                ]}
            />

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <Pagination.PageSizeDropdown
                                pageSize={pagination.pageSize}
                                onPageSizeChange={setPageSize}
                            />
                            <FormProvider {...formMethods}>
                                <OrderSourceFilters />
                            </FormProvider>
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columns}
                            onColumnVisibilityChange={changeColumnVisibility}
                            disabledColumns={disabledColumns}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {!loading && data.length === 0 ? (
                        <NoData
                            title="No order sources yet"
                            btnText="Add source"
                            openCreateDialog={openCreateDialog}
                            createPermission="order_sources:create"
                        />
                    ) : (
                        <>
                            <DataTable columns={columns} data={data} loading={loading} />
                            <Pagination.Buttons
                                currentPage={pagination.currentPage}
                                totalItems={pagination.totalCount}
                                pageSize={pagination.pageSize}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <OrderSourceDialog
                open={orderSourceDialogOpen}
                mode={mode}
                editOrderSource={editOrderSource}
                closeDialog={closeDialog}
                onCreate={handleCreateOrderSource}
                onUpdate={handleUpdateOrderSource}
                loading={createLoading || updateLoading}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete source?"
                description={`"${orderSourceToDelete?.name}" will be removed from your order sources. Existing orders keep their source, but new orders can't use it.`}
                confirmText={deleteLoading ? 'Deleting…' : 'Delete source'}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
                variant="destructive"
            />
        </div>
    )
}
